/**
 * MemberProgramWorkspace — the member's one activity view (TRUS-762).
 *
 * One chronological feed (MemberActivityFeed in scoped mode) under one toolbar:
 *
 *   [Filter ▾ (n)]  [Case: All ▾]  ……………………  [Add ▾]
 *
 *   - Filter opens a popover: record types (checkboxes, with counts), the org's
 *     note categories, a date range, and Clear. Whatever is selected shows as
 *     small removable chips under the toolbar; nothing selected, nothing shown.
 *     The selection is remembered per user: a saved default on the server
 *     (activityFilterPrefs, follows the user across devices), and the last
 *     used filter in localStorage as the fallback.
 *   - Case only exists when the org has the `case_management` capability. With
 *     it, the member's open case's details sit above the feed and new records
 *     are filed under it, but the feed is NOT narrowed to it: Case defaults to
 *     All. Without it there is no case UI at all — a plain activity feed.
 *   - Add opens the existing creation flows (note, task, document, form,
 *     letter, outreach, referral), the member communications (call, text,
 *     email), and — with case management — a new case.
 *
 * Rows open in place (MemberActivityFeed); "Open" goes to the full surface:
 * a task opens as a screen, a form the form editor, a document its file, a
 * workflow its instance, a referral the hub, a DM the inbox. Email/SMS/phone
 * rows expand inline only.
 *
 * Every Add action and every Open that stays on the profile is a SCREEN, not a
 * modal (TRUS-762 round 3): it replaces the toolbar and feed with a header
 * (title, "Back to activities", the case/program it files under) and the form
 * below. Saving or cancelling comes back to the feed and refreshes it.
 *
 * Old work-tab id → what it is now (flags, tabConfig and record deep links all
 * go through this mapping):
 *   case-episodes → the case header (no kind)
 *   activities    → type `activity`; the flag also gates note, encounter,
 *                   workflow and member_update
 *   conversations → type `message`; the flag gates message, outreach and the
 *                   Call / Text / Email adds
 *   outreach      → type `outreach`
 *   documents     → type `document`
 *   forms         → type `form`
 *   mail          → type `letter`
 *   tasks         → type `task`
 * Referrals had no work tab and are always shown.
 *
 * Works against an API that predates the Phase 2 contract: without `cases` the
 * case list comes from caseEpisodesAPI; without `counts` the type list carries
 * no numbers and only kinds actually seen are offered; without `can_move`
 * there is no Move.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft, Bookmark, Briefcase, CalendarRange, ChevronDown, FilePlus, Filter, FolderOpen, ListChecks,
  Loader2, Mail, MailPlus, Megaphone, MessageSquare, Phone, Plus, RotateCcw, Settings2, Share2, StickyNote, Tag, X,
} from 'lucide-react';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ACCENT_BUTTON, INK_GHOST_BUTTON } from '@/components/ui/accent';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { membersAPI, Enrollment, Member } from '@/services/api/members';
import { caseEpisodesAPI } from '@/services/api/caseEpisodes';
import {
  FEED_KINDS,
  type ActivityFeedItem,
  type ActivityFeedResponse,
  type FeedCase,
  type FeedCaseFilter,
  type FeedKind,
  type NoteCategory,
} from '@/services/api/memberActivityFeed';
import { getWeaveinApiUrl } from '@/src/config';
import { requestOpenRecord } from '@/lib/recordDeepLinks';
import { getApiErrorMessage } from '@/utils/apiErrors';
import {
  EMPTY_FILTERS,
  type FeedFilters,
  isEmptyFilters,
  loadSavedActivityFilters,
  readLocalFilters as readFilters,
  resetActivityFiltersDefault,
  saveActivityFiltersDefault,
  writeLocalFilters as writeFilters,
} from './activityFilterPrefs';
import { LetterComposer } from '@/components/communications/LetterComposer';
import { MemberPhoneInterface } from '@/components/communications/MemberPhoneInterface';
import { MemberMessageComposer } from '@/components/communications/MemberMessageComposer';
import { DocumentUploadDialog } from '@/components/documents/DocumentUploadDialog';
import { FormInstanceEditor } from '@/components/forms/FormInstanceEditor';
import MemberFormsHub from '@/components/forms/MemberFormsHub';
import { ServiceReferralCreateForm } from '@/components/referral-hub/ServiceReferralCreateForm';
import TaskForm from '@/components/tasks/TaskForm';
import { TaskDetailView } from '@/components/tasks/TaskDetailDialog';
import MemberActivityFeed, { isPhoneItem } from './MemberActivityFeed';
import MemberNoteComposer from './MemberNoteComposer';
import MemberCaseEpisodes from './MemberCaseEpisodes';
import MemberOutreachHistoryTab from './MemberOutreachHistoryTab';
import MemberOutreachTrackingTab from './MemberOutreachTrackingTab';
import { useCapability } from '@/hooks/useCapabilities';

interface Props {
  memberId: number;
  memberName?: string;
  member?: Member;
  onNavigate?: (tab: string, id?: number) => void;
  show?: {
    caseEpisodes?: boolean;
    activities?: boolean;
    conversations?: boolean;
    documents?: boolean;
    forms?: boolean;
    tasks?: boolean;
    mail?: boolean;
  };
  /**
   * Per-org wording and ordering, keyed by the old work-tab id (TRUS-528).
   * Mapped onto the record types (see the header). Absent or partial means
   * "platform default", so an org that configured nothing gets the defaults.
   */
  tabConfig?: Partial<Record<WorkTab, { label?: string; order?: number }>>;
  /** TRUS-685: a record link asked for this work tab; selects the matching type. */
  requestedWorkTab?: { tab: WorkTab; at: number } | null;
}

type WorkTab = 'case-episodes' | 'activities' | 'conversations' | 'outreach' | 'documents' | 'forms' | 'tasks' | 'mail';
type FlagKey = 'activities' | 'conversations' | 'documents' | 'forms' | 'mail' | 'tasks';

/** Which kinds each org show-flag gates. Referral has no flag (always shown). */
const FLAG_KINDS: Record<FlagKey, FeedKind[]> = {
  activities: ['note', 'activity', 'encounter', 'workflow', 'member_update'],
  conversations: ['message', 'outreach'],
  documents: ['document'],
  forms: ['form'],
  mail: ['letter'],
  tasks: ['task'],
};

/** Old work tab → the type that takes its label, order and deep links. */
const TAB_KIND: Partial<Record<WorkTab, FeedKind>> = {
  activities: 'activity',
  conversations: 'message',
  outreach: 'outreach',
  documents: 'document',
  forms: 'form',
  mail: 'letter',
  tasks: 'task',
};

/** Default type order, on the same scale as the profile-layout registry
 *  (case-episodes 10, activities 20 … tasks 80) so an org's reordering of the
 *  old tabs slots in among the kinds that never had one. */
const DEFAULT_ORDER: Record<FeedKind, number> = {
  note: 15, activity: 20, encounter: 25, message: 30, outreach: 40, document: 50,
  form: 60, letter: 70, task: 80, referral: 85, workflow: 90, member_update: 95,
};

const CHIP_LABEL: Record<FeedKind, [string, string]> = {
  note: ['memberWork.chip.note', 'Notes'],
  activity: ['memberWork.chip.activity', 'Activities'],
  task: ['memberWork.chip.task', 'Tasks'],
  document: ['memberWork.chip.document', 'Documents'],
  form: ['memberWork.chip.form', 'Forms'],
  message: ['memberWork.chip.message', 'Messages'],
  outreach: ['memberWork.chip.outreach', 'Outreach'],
  letter: ['memberWork.chip.letter', 'Letters'],
  encounter: ['memberWork.chip.encounter', 'Encounters'],
  referral: ['memberWork.chip.referral', 'Referrals'],
  workflow: ['memberWork.chip.workflow', 'Workflows'],
  member_update: ['memberWork.chip.member_update', 'Profile updates'],
};

/** Kinds that are only offered when the member actually has some. */
const ONLY_IF_PRESENT: FeedKind[] = ['workflow', 'member_update'];

const OPEN_STATUSES = ['NEW', 'ACTIVE', 'SUSPENDED'];
const isOpenCase = (c: FeedCase) => c.is_current && OPEN_STATUSES.includes(String(c.status).toUpperCase());

/** Feed ids are namespaced ("task:44"); an older API has no record_id. */
function recordIdOf(item: ActivityFeedItem): number | null {
  if (item.record_id != null) return item.record_id;
  const n = Number((item.id || '').split(':')[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** A screen that replaces the toolbar and feed. Exported for tests. */
export type Panel =
  | { type: 'form'; instanceId: number; programId?: number; enrollmentId?: number }
  | { type: 'forms' }
  | { type: 'note' }
  | { type: 'task' }
  | { type: 'task-view'; id: number; caseLabel: string | null; programName: string | null }
  | { type: 'document' }
  | { type: 'letter' }
  | { type: 'outreach' }
  | { type: 'referral' }
  | { type: 'call' }
  | { type: 'sms' }
  | { type: 'email' }
  | { type: 'new-case' };

export default function MemberProgramWorkspace({ memberId, memberName, member, onNavigate, show, tabConfig, requestedWorkTab }: Props) {
  const { t } = useAppTranslation();
  const { user } = useAuth();
  const userId = (user as any)?.id ?? null;

  // --- state (all declared before any hook that reads it) -------------------
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [cases, setCases] = useState<FeedCase[]>([]);
  /** True once the feed itself returned `cases` (Phase 2 API). */
  const [casesFromFeed, setCasesFromFeed] = useState(false);
  const [canMove, setCanMove] = useState(false);
  const [booting, setBooting] = useState(true);
  const [caseFilter, setCaseFilter] = useState<FeedCaseFilter>('all');
  const [filters, setFilters] = useState<FeedFilters>(() => readFilters(userId));
  const [categories, setCategories] = useState<NoteCategory[]>([]);
  const [canManageCategories, setCanManageCategories] = useState(false);
  const [manageCategoriesRequest, setManageCategoriesRequest] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);
  const [counts, setCounts] = useState<Partial<Record<FeedKind, number>> | null>(null);
  const [seenKinds, setSeenKinds] = useState<FeedKind[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [panel, setPanel] = useState<Panel | null>(null);
  /** Enrollment for Add flows that need one when no case is picked. */
  const [addEnrollmentId, setAddEnrollmentId] = useState<number | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  /** The case fallback (older API) has been asked for once this member. */
  const fallbackAsked = useRef(false);

  // Outreach mode: billing view when the org has `outreach_billing`, else
  // billing-agnostic touch tracking. Hooks run before any early return.
  const activeCboId = parseInt(localStorage.getItem('active_cbo_id') || '0', 10) || 0;
  const { enabled: outreachBillingEnabled } = useCapability(activeCboId || null, 'outreach_billing');
  // Cases are a capability, not a given: an org without it sees a plain feed.
  const { enabled: caseManagementEnabled } = useCapability(activeCboId || null, 'case_management');

  const flags = {
    caseEpisodes: show?.caseEpisodes !== false && caseManagementEnabled,
    activities: show?.activities !== false,
    conversations: show?.conversations !== false,
    documents: show?.documents !== false,
    forms: show?.forms !== false,
    tasks: show?.tasks !== false,
    mail: show?.mail !== false,
  };
  const flagsKey = JSON.stringify(flags);

  /** Kinds this org shows on the member profile. */
  const visibleKinds = useMemo(() => {
    const hidden = new Set<FeedKind>();
    (Object.keys(FLAG_KINDS) as FlagKey[]).forEach((f) => {
      if (!flags[f]) FLAG_KINDS[f].forEach((k) => hidden.add(k));
    });
    return FEED_KINDS.filter((k) => !hidden.has(k));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flagsKey]);

  /** Record types in the org's order and wording. */
  const typeOptions = useMemo(() => {
    const kindCfg: Partial<Record<FeedKind, { label?: string; order?: number }>> = {};
    (Object.keys(TAB_KIND) as WorkTab[]).forEach((tab) => {
      const cfg = tabConfig?.[tab];
      const kind = TAB_KIND[tab];
      if (cfg && kind) kindCfg[kind] = cfg;
    });
    return visibleKinds
      .map((k) => ({
        kind: k,
        label: kindCfg[k]?.label || t(CHIP_LABEL[k][0], CHIP_LABEL[k][1]),
        order: kindCfg[k]?.order ?? DEFAULT_ORDER[k],
      }))
      .sort((a, b) => a.order - b.order || DEFAULT_ORDER[a.kind] - DEFAULT_ORDER[b.kind]);
  }, [visibleKinds, tabConfig, t]);

  // --- filters ---------------------------------------------------------------

  // A different user on the same browser gets their own remembered filter.
  const filtersUser = useRef(userId);
  useEffect(() => {
    if (filtersUser.current === userId) return;
    filtersUser.current = userId;
    setFilters(readFilters(userId));
  }, [userId]);

  /** Set once the user edits the filter, so a late saved default never overwrites their choice. */
  const filtersTouched = useRef(false);
  const updateFilters = (next: FeedFilters) => {
    filtersTouched.current = true;
    setFilters(next);
    writeFilters(userId, next);
  };

  // TRUS-762 round 4: the user's saved default lives on the server so it
  // follows them across devices. Loaded once per user; if the call fails the
  // browser copy above stays in force.
  const [hasSavedDefault, setHasSavedDefault] = useState(false);
  const [defaultStatus, setDefaultStatus] = useState<'idle' | 'saving' | 'saved' | 'reset' | 'error'>('idle');
  useEffect(() => {
    let cancelled = false;
    filtersTouched.current = false;
    loadSavedActivityFilters()
      .then((saved) => {
        if (cancelled) return;
        setHasSavedDefault(!!saved);
        if (!saved || filtersTouched.current) return;
        setFilters(saved.filters);
        writeFilters(userId, saved.filters);
        if (saved.caseMode === 'none') setCaseFilter('none');
      })
      .catch(() => {
        // Store unreachable: keep the browser copy.
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const saveFiltersAsDefault = async () => {
    setDefaultStatus('saving');
    try {
      await saveActivityFiltersDefault({ filters, caseMode: caseFilter === 'none' ? 'none' : 'all' });
      setHasSavedDefault(!isEmptyFilters(filters) || caseFilter === 'none');
      setDefaultStatus('saved');
    } catch {
      setDefaultStatus('error');
    }
  };

  const resetFiltersDefault = async () => {
    setDefaultStatus('saving');
    try {
      await resetActivityFiltersDefault();
      setHasSavedDefault(false);
      updateFilters({ ...EMPTY_FILTERS });
      setCaseFilter('all');
      setDefaultStatus('reset');
    } catch {
      setDefaultStatus('error');
    }
  };

  /** Types that are actually in force: hidden kinds never count. */
  const selectedTypes = filters.types.filter((k) => visibleKinds.includes(k));
  const activeCategory = filters.categoryId != null && flags.activities
    ? categories.find((c) => c.id === filters.categoryId && c.is_active) || null
    : null;

  const toggleType = (kind: FeedKind) => {
    const on = selectedTypes.includes(kind);
    const types = on ? selectedTypes.filter((k) => k !== kind) : [...selectedTypes, kind];
    // A category only exists on notes: turning notes off, or picking a type
    // other than notes, drops it instead of silently returning nothing.
    const keepCategory = activeCategory && types.length > 0 && types.every((k) => k === 'note');
    updateFilters({ ...filters, types, categoryId: keepCategory ? filters.categoryId : null });
  };

  const toggleCategory = (id: number) => {
    if (filters.categoryId === id) {
      updateFilters({ ...filters, categoryId: null });
      return;
    }
    // Picking a category means "notes in this category".
    updateFilters({ ...filters, categoryId: id, types: ['note'] });
  };

  const clearFilters = () => updateFilters({ ...EMPTY_FILTERS });

  const activeFilterCount =
    selectedTypes.length + (activeCategory ? 1 : 0) + (filters.dateFrom || filters.dateTo ? 1 : 0);

  // --- data ----------------------------------------------------------------

  /** Cases from caseEpisodesAPI, for an API that does not return `cases`. */
  const loadCasesFallback = useCallback(async (enrs: Enrollment[]): Promise<FeedCase[]> => {
    const enrollmentFor = (programId: number) =>
      enrs.find((e) => e.program_id === programId && e.status === 'active') ||
      enrs.find((e) => e.program_id === programId);
    try {
      const history = await caseEpisodesAPI.getMemberHistory(memberId);
      return (history.episodes || []).map((e) => ({
        id: e.id,
        case_number: e.case_number,
        episode_number: e.episode_number,
        status: e.status,
        is_current: e.is_current,
        program_id: e.program_id,
        program_name: e.program_name,
        enrollment_id: enrollmentFor(e.program_id)?.id ?? null,
        opened_date: e.opened_date ?? null,
        closed_date: e.closed_date ?? null,
      }));
    } catch {
      // Per program, from the enrollments, as the old workspace did.
      const programIds = Array.from(new Set(enrs.map((e) => e.program_id)));
      const lists = await Promise.all(
        programIds.map((pid) =>
          caseEpisodesAPI
            .listEpisodes({ member_id: memberId, program_id: pid, include_archived: true })
            .catch(() => []),
        ),
      );
      return lists.flat().map((e) => ({
        id: e.id,
        case_number: e.case_number,
        episode_number: e.episode_number,
        status: e.status,
        is_current: e.is_current,
        program_id: e.program_id,
        program_name: e.program_name ?? enrs.find((x) => x.program_id === e.program_id)?.program_name ?? null,
        enrollment_id: e.enrollment_id ?? enrollmentFor(e.program_id)?.id ?? null,
        opened_date: e.opened_date ?? null,
        closed_date: e.closed_date ?? null,
      }));
    }
  }, [memberId]);

  useEffect(() => {
    let cancelled = false;
    fallbackAsked.current = false;
    setBooting(true);
    setCaseFilter('all');
    setCases([]);
    setCasesFromFeed(false);
    setCanMove(false);
    setPanel(null);
    setCounts(null);
    setSeenKinds([]);
    (async () => {
      const rows = await membersAPI.getMemberEnrollments(memberId).catch(() => [] as Enrollment[]);
      const sorted = (rows || []).slice().sort((a, b) => {
        if (a.status === 'active' && b.status !== 'active') return -1;
        if (b.status === 'active' && a.status !== 'active') return 1;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
      if (cancelled) return;
      setEnrollments(sorted);
      setAddEnrollmentId(sorted[0]?.id ?? null);
      // Cases arrive with the first feed response (or the fallback below); no
      // separate request for them.
      setBooting(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [memberId]);

  // TRUS-685 record links: a work tab selects its type; case-episodes shows the open case.
  useEffect(() => {
    if (!requestedWorkTab) return;
    setPanel(null);
    const kind = TAB_KIND[requestedWorkTab.tab];
    if (kind) {
      if (visibleKinds.includes(kind)) {
        // Every case, so the linked record is on screen whichever case it is in.
        setCaseFilter('all');
        updateFilters({ ...EMPTY_FILTERS, types: [kind] });
      }
      return;
    }
    if (requestedWorkTab.tab === 'case-episodes') {
      updateFilters({ ...filters, types: [], categoryId: null });
      setCaseFilter('all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedWorkTab]);

  // Without case management the feed is never narrowed by case.
  useEffect(() => {
    if (!flags.caseEpisodes && caseFilter !== 'all') setCaseFilter('all');
  }, [flags.caseEpisodes, caseFilter]);

  const enrollmentsRef = useRef<Enrollment[]>([]);
  enrollmentsRef.current = enrollments;

  const handleFeedResponse = useCallback((res: ActivityFeedResponse) => {
    setCounts(res.counts ?? null);
    if (Array.isArray(res.cases)) {
      setCases(res.cases);
      setCasesFromFeed(true);
    } else if (!fallbackAsked.current) {
      // An API that predates `cases`: ask the case API once.
      fallbackAsked.current = true;
      setCasesFromFeed(false);
      void loadCasesFallback(enrollmentsRef.current).then(setCases).catch(() => setCases([]));
    }
    setCanMove(typeof res.can_move === 'boolean' ? res.can_move : false);
    const kinds = (res.items || []).map((i) => i.kind as FeedKind);
    setSeenKinds((prev) => {
      const next = Array.from(new Set([...prev, ...kinds]));
      return next.length === prev.length ? prev : next;
    });
  }, [loadCasesFallback]);

  const handleCategories = useCallback((list: NoteCategory[], canManage: boolean) => {
    setCategories(list);
    setCanManageCategories(canManage);
  }, []);

  const refresh = () => setRefreshKey((k) => k + 1);

  // --- derived ---------------------------------------------------------------
  const caseUi = flags.caseEpisodes;
  const visibleCases = caseUi ? cases : [];
  const selectedCase = caseUi && typeof caseFilter === 'number' ? cases.find((c) => c.id === caseFilter) || null : null;
  /** The member's open case: the first program's (enrollments are active-first, newest). */
  const openCase = useMemo(() => {
    if (!caseUi) return null;
    const byProgram = enrollments
      .map((e) => cases.find((c) => c.program_id === e.program_id && isOpenCase(c)))
      .find(Boolean);
    return byProgram || cases.find(isOpenCase) || null;
  }, [caseUi, cases, enrollments]);
  /** The case whose details sit above the feed: the picked one, else (under All) the open one. */
  const headerCase = selectedCase || (caseFilter === 'all' ? openCase : null);
  /** New records are filed under the picked case, else the open one. */
  const caseForNew = caseUi ? (selectedCase || (caseFilter === 'all' ? openCase : null)) : null;
  const caseIdForNew = caseForNew?.id ?? null;
  const multiProgram = new Set(visibleCases.map((c) => c.program_id)).size > 1;

  /** The enrollment a new record goes to: its case's, else the chosen one. */
  const addEnrollment: Enrollment | null = caseForNew
    ? enrollments.find((e) => e.id === caseForNew.enrollment_id) ||
      enrollments.find((e) => e.program_id === caseForNew.program_id && e.status === 'active') ||
      enrollments.find((e) => e.program_id === caseForNew.program_id) ||
      null
    : enrollments.find((e) => e.id === addEnrollmentId) || enrollments[0] || null;

  // A category means notes; otherwise the picked types, else every visible kind.
  const requestTypes: FeedKind[] | null = activeCategory
    ? ['note']
    : selectedTypes.length
      ? selectedTypes
      : visibleKinds.length === FEED_KINDS.length
        ? null
        : visibleKinds;

  const shownTypes = typeOptions.filter((c) => {
    if (selectedTypes.includes(c.kind)) return true;
    if (!counts) return seenKinds.includes(c.kind);
    if (ONLY_IF_PRESENT.includes(c.kind)) return (counts[c.kind] ?? 0) > 0;
    return true;
  });
  const typeLabel = (k: FeedKind) => typeOptions.find((o) => o.kind === k)?.label || t(CHIP_LABEL[k][0], CHIP_LABEL[k][1]);

  const caseName = (c: FeedCase) => t('memberWork.caseChip', 'Case #{{number}}', { number: c.case_number });
  const caseFilterLabel =
    caseFilter === 'all'
      ? t('memberWork.allCases', 'All')
      : caseFilter === 'none'
        ? t('memberWork.noCase', 'No case')
        : selectedCase
          ? caseName(selectedCase)
          : t('memberWork.allCases', 'All');

  const chooseCase = (next: FeedCaseFilter) => {
    setPanel(null);
    setCaseFilter(next);
  };

  const handleEpisodeChange = (current: { id: number; status: string } | null) => {
    // Closing, reopening or creating a case in the header changes the list.
    if (!current) return;
    if (!cases.some((c) => c.id === current.id && c.status === current.status)) {
      if (casesFromFeed) refresh();
      else void loadCasesFallback(enrollments).then(setCases).catch(() => undefined);
    }
  };

  // --- opening a row -------------------------------------------------------
  const openDocument = async (item: ActivityFeedItem) => {
    const id = recordIdOf(item);
    const url: string | undefined =
      item.meta?.document_url || (item.record_type !== 'enrollment_document' && id ? `/api/documents/${id}/download` : undefined);
    if (!url) return;
    if (/^https?:\/\//.test(url)) {
      window.open(url, '_blank', 'noopener');
      return;
    }
    try {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      const res = await fetch(getWeaveinApiUrl(url), {
        credentials: 'include',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(cboId ? { 'X-CBO-ID': cboId } : {}),
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const contentType = (res.headers.get('content-type') || '').toLowerCase();
      if (contentType.includes('application/json')) {
        const data = await res.json();
        if (data?.download_url) window.open(data.download_url, '_blank', 'noopener');
        return;
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      if (contentType.startsWith('image/') || contentType === 'application/pdf' || contentType.startsWith('text/plain')) {
        window.open(blobUrl, '_blank');
      } else {
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = item.title || `document-${id}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      }
    } catch (e) {
      setOpenError(getApiErrorMessage(e) || t('memberWork.openFailed', 'Could not open that record.'));
    }
  };

  const canOpen = (item: ActivityFeedItem): boolean => {
    const id = recordIdOf(item);
    switch (item.kind) {
      case 'task':
      case 'form':
        return id != null;
      case 'document':
        return !!item.meta?.document_url || (id != null && item.record_type !== 'enrollment_document');
      case 'referral':
        return !!onNavigate;
      case 'message':
        // Only the member DM has a home of its own; email, SMS and calls open in place.
        return !!onNavigate && item.meta?.source === 'member_dm' && !isPhoneItem(item);
      case 'workflow':
        return !!item.meta?.workflow_instance_id;
      default:
        return false;
    }
  };

  const openItem = async (item: ActivityFeedItem) => {
    setOpenError(null);
    const id = recordIdOf(item);
    switch (item.kind) {
      case 'task':
        if (id == null) return;
        setPanel({
          type: 'task-view',
          id,
          caseLabel: caseUi && item.case_number ? t('memberWork.caseChip', 'Case #{{number}}', { number: item.case_number }) : null,
          programName: item.program_name ?? item.meta?.program_name ?? null,
        });
        return;
      case 'form':
        if (id == null) return;
        setPanel({
          type: 'form',
          instanceId: id,
          programId: item.program_id ?? undefined,
          enrollmentId: item.enrollment_id ?? item.meta?.enrollment_id ?? undefined,
        });
        return;
      case 'document':
        await openDocument(item);
        return;
      case 'referral':
        // The closed-loop referral hub (the profile cannot hand it a referral id).
        onNavigate?.('v2-referrals');
        return;
      case 'message':
        onNavigate?.('workspace-messaging');
        return;
      case 'workflow':
        if (item.meta?.workflow_instance_id) {
          requestOpenRecord({
            kind: 'workflow-instance',
            tab: 'workflow-instances',
            instanceId: Number(item.meta.workflow_instance_id),
          });
        }
        return;
      default:
        return;
    }
  };

  // --- render ----------------------------------------------------------------
  if (booting) {
    return (
      <div className="flex items-center gap-2 py-8 justify-center text-ink-500">
        <Loader2 className="w-4 h-4 animate-spin" /> {t('memberWork.loading', 'Loading activity…')}
      </div>
    );
  }

  const enrollmentPicker = (
    <div className="flex items-center gap-3 flex-wrap">
      <label className="text-sm font-medium text-ink">
        {t('members.programEnrollment', 'Program enrollment:')}
      </label>
      <Select value={addEnrollmentId ? String(addEnrollmentId) : ''} onValueChange={(v) => setAddEnrollmentId(Number(v))}>
        <SelectTrigger className="w-80">
          <SelectValue placeholder={t('members.selectEnrollment', 'Select an enrollment')} />
        </SelectTrigger>
        <SelectContent>
          {enrollments.map((e) => (
            <SelectItem key={e.id} value={String(e.id)}>
              {e.program_name || t('memberWork.programNumber', 'Program #{{id}}', { id: e.program_id })} · {e.status}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  /** Leave a screen for the feed; anything it saved shows on return. */
  const closeScreen = () => {
    setPanel(null);
    refresh();
  };

  const memberLabel = memberName || (member ? `${member.first_name || ''} ${member.last_name || ''}`.trim() : '');

  const screenTitle = (p: Panel): string => {
    switch (p.type) {
      case 'form': return t('memberWork.screen.form', 'Form');
      case 'forms': return t('memberWork.screen.forms', 'Forms');
      case 'note': return t('memberWork.screen.note', 'New note');
      case 'task': return t('memberWork.screen.task', 'New task');
      case 'task-view': return t('memberWork.screen.taskView', 'Task');
      case 'document': return t('memberWork.screen.document', 'Upload document');
      case 'letter': return t('memberWork.screen.letter', 'Send a letter');
      case 'outreach': return t('memberWork.screen.outreach', 'Log outreach');
      case 'referral': return t('memberWork.screen.referral', 'New referral');
      case 'call': return t('memberWork.callTitle', 'Call {{name}}', { name: memberLabel });
      case 'sms': return t('memberWork.smsTitle', 'Text {{name}}', { name: memberLabel });
      case 'email': return t('memberWork.emailTitle', 'Email {{name}}', { name: memberLabel });
      case 'new-case': return t('memberWork.screen.newCase', 'New case');
      default: return '';
    }
  };

  /** What the screen files under: the case and program, or the program alone. */
  const screenContext = (p: Panel): string | null => {
    if (p.type === 'task-view') return [p.caseLabel, p.programName].filter(Boolean).join(' · ') || null;
    if (p.type === 'form') {
      const e = enrollments.find((x) => x.id === p.enrollmentId) || enrollments.find((x) => x.program_id === p.programId);
      return e?.program_name || null;
    }
    const program = addEnrollment?.program_name || null;
    if (caseForNew) return [caseName(caseForNew), caseForNew.program_name || program].filter(Boolean).join(' · ');
    return program;
  };

  const renderScreenBody = (p: Panel) => {
    switch (p.type) {
      case 'form':
        return (
          <FormInstanceEditor
            instanceId={p.instanceId}
            memberId={memberId}
            programId={p.programId}
            organizationId={activeCboId || undefined}
            onClose={closeScreen}
          />
        );
      case 'forms':
        return (
          <div className="space-y-3">
            {!caseForNew && enrollments.length > 1 && enrollmentPicker}
            <MemberFormsHub
              key={addEnrollment?.id ?? 'none'}
              memberId={memberId}
              enrollmentId={addEnrollment?.id}
              programId={addEnrollment?.program_id}
              caseEpisodeId={caseIdForNew}
              organizationId={activeCboId || undefined}
              onOpenInstance={(instanceId) =>
                setPanel({
                  type: 'form',
                  instanceId,
                  programId: addEnrollment?.program_id,
                  enrollmentId: addEnrollment?.id,
                })
              }
            />
          </div>
        );
      case 'note':
        return (
          <MemberNoteComposer
            memberId={memberId}
            enrollmentId={caseForNew ? addEnrollment?.id : undefined}
            caseEpisodeId={caseIdForNew}
            categories={categories}
            onSaved={closeScreen}
            onCancel={closeScreen}
          />
        );
      case 'task':
        return (
          <TaskForm
            variant="page"
            memberId={memberId}
            task={null}
            caseEpisodeId={caseIdForNew}
            onClose={closeScreen}
            onSaved={closeScreen}
          />
        );
      case 'task-view':
        return (
          <TaskDetailView
            key={p.id}
            taskId={p.id}
            memberId={memberId}
            caseLabel={p.caseLabel}
            programName={p.programName}
            onClose={closeScreen}
            onChanged={refresh}
          />
        );
      case 'document':
        return (
          <DocumentUploadDialog
            variant="page"
            open
            onOpenChange={(open) => { if (!open) closeScreen(); }}
            targetType="member"
            targetId={memberId}
            targetName={memberName}
            defaultEnrollmentId={addEnrollment?.id}
            defaultProgramId={addEnrollment?.program_id}
            caseEpisodeId={caseIdForNew}
            onUploadComplete={refresh}
          />
        );
      case 'letter':
        return (
          <LetterComposer
            variant="page"
            memberId={memberId}
            memberName={memberName}
            memberAddress={
              member
                ? {
                    street_address: member.street_address || '',
                    city: member.city || '',
                    state: member.state || '',
                    zip_code: member.zip_code || '',
                  }
                : undefined
            }
            programId={addEnrollment?.program_id}
            enrollmentId={addEnrollment?.id}
            caseEpisodeId={caseIdForNew}
            onClose={closeScreen}
            onSent={refresh}
          />
        );
      case 'outreach':
        return outreachBillingEnabled ? (
          <MemberOutreachHistoryTab memberId={memberId} />
        ) : (
          <MemberOutreachTrackingTab memberId={memberId} memberName={memberName} enrollments={enrollments} />
        );
      case 'referral':
        return (
          <ServiceReferralCreateForm
            memberId={memberId}
            caseEpisodeId={caseIdForNew}
            onCancel={closeScreen}
            onDone={closeScreen}
          />
        );
      case 'call':
        return member ? (
          <div className="rounded-lg border border-v2border bg-surface p-4 sm:p-6">
            <MemberPhoneInterface member={member} enrollmentId={addEnrollment?.id} />
          </div>
        ) : null;
      case 'sms':
      case 'email':
        return member ? (
          <div className="rounded-lg border border-v2border bg-surface p-4 sm:p-6">
            <MemberMessageComposer
              key={p.type}
              hideHeading
              member={member}
              channel={p.type}
              enrollmentId={addEnrollment?.id ?? null}
              programId={addEnrollment?.program_id ?? null}
              onCancel={closeScreen}
              onSent={closeScreen}
            />
          </div>
        ) : null;
      case 'new-case':
        return (
          <div className="space-y-3">
            {enrollments.length > 1 && enrollmentPicker}
            {addEnrollment && (
              <MemberCaseEpisodes
                key={addEnrollment.id}
                memberId={memberId}
                programId={addEnrollment.program_id}
                cboId={activeCboId}
                enrollmentId={addEnrollment.id}
                onNavigate={onNavigate}
                onEpisodeChange={handleEpisodeChange}
              />
            )}
          </div>
        );
      default:
        return null;
    }
  };

  /** The screen: header (title, Back, what it files under), then the form. */
  const renderPanel = (p: Panel) => {
    const context = screenContext(p);
    return (
      <section className="space-y-4" aria-label={screenTitle(p)}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-v2border pb-3">
          <Button size="sm" variant="ghost" className={INK_GHOST_BUTTON} onClick={closeScreen}>
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            {t('memberWork.backToActivities', 'Back to activities')}
          </Button>
          <h3 className="text-base font-semibold text-ink">{screenTitle(p)}</h3>
          {context && <span className="text-xs text-ink-500">{context}</span>}
        </div>
        {renderScreenBody(p)}
      </section>
    );
  };

  const hasPhone = !!(member?.phone_no || member?.phone_number);
  const addItems: Array<{ id: string; icon: React.ElementType; label: string; run: () => void; show: boolean; disabled?: boolean }> = [
    { id: 'note', icon: StickyNote, label: t('memberWork.add.note', 'Note'), show: flags.activities,
      run: () => {
        // The saved note has to be visible under the filter that is on.
        if (selectedTypes.length && !selectedTypes.includes('note')) updateFilters({ ...filters, types: [] });
        setPanel({ type: 'note' });
      } },
    { id: 'task', icon: ListChecks, label: t('memberWork.add.task', 'Task'), show: flags.tasks,
      run: () => setPanel({ type: 'task' }) },
    { id: 'document', icon: FolderOpen, label: t('memberWork.add.document', 'Upload document'), show: flags.documents,
      run: () => setPanel({ type: 'document' }) },
    { id: 'form', icon: Briefcase, label: t('memberWork.add.form', 'Form'), show: flags.forms,
      run: () => setPanel({ type: 'forms' }) },
    { id: 'letter', icon: Mail, label: t('memberWork.add.letter', 'Letter'), show: flags.mail,
      run: () => setPanel({ type: 'letter' }) },
    { id: 'outreach', icon: Megaphone, label: t('memberWork.add.outreach', 'Log outreach'), show: flags.conversations,
      run: () => setPanel({ type: 'outreach' }) },
    { id: 'referral', icon: Share2, label: t('memberWork.add.referral', 'Referral'), show: true,
      run: () => setPanel({ type: 'referral' }) },
  ];
  const commsItems: typeof addItems = [
    { id: 'call', icon: Phone, label: t('memberWork.add.call', 'Call'), show: flags.conversations && !!member,
      disabled: !hasPhone, run: () => setPanel({ type: 'call' }) },
    { id: 'sms', icon: MessageSquare, label: t('memberWork.add.sms', 'Text message'), show: flags.conversations && !!member,
      disabled: !hasPhone, run: () => setPanel({ type: 'sms' }) },
    { id: 'email', icon: MailPlus, label: t('memberWork.add.email', 'Email'), show: flags.conversations && !!member,
      disabled: !member?.email, run: () => setPanel({ type: 'email' }) },
  ];
  const visibleAdd = addItems.filter((a) => a.show);
  const visibleComms = commsItems.filter((a) => a.show);
  const canAddCase = caseUi && enrollments.length > 0;

  const renderAddItem = (a: (typeof addItems)[number]) => {
    const Icon = a.icon;
    return (
      <DropdownMenuItem key={a.id} onSelect={a.run} disabled={a.disabled}>
        <Icon className="w-4 h-4 mr-2 text-ink-500" />
        {a.label}
      </DropdownMenuItem>
    );
  };

  const chip = (key: string, label: React.ReactNode, onRemove: () => void) => (
    <span
      key={key}
      className="inline-flex items-center gap-1 rounded-md border border-v2border bg-surface pl-2 pr-1 py-0.5 text-xs text-ink"
    >
      {label}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onRemove}
        aria-label={t('memberWork.filter.remove', 'Remove filter')}
        className={`${INK_GHOST_BUTTON} !p-0.5 !rounded`}
      >
        <X className="w-3 h-3" />
      </Button>
    </span>
  );

  const dateChipLabel =
    filters.dateFrom && filters.dateTo
      ? t('memberWork.filter.dateBetween', '{{from}} – {{to}}', { from: filters.dateFrom, to: filters.dateTo })
      : filters.dateFrom
        ? t('memberWork.filter.dateFromOnly', 'From {{from}}', { from: filters.dateFrom })
        : t('memberWork.filter.dateToOnly', 'Until {{to}}', { to: filters.dateTo });

  const activeCategories = categories.filter((c) => c.is_active);

  return (
    <div className="space-y-4">
      {/* The open (or picked) case's details — case management only. */}
      {headerCase && !panel && (
        <MemberCaseEpisodes
          key={headerCase.id}
          memberId={memberId}
          programId={headerCase.program_id}
          cboId={activeCboId}
          enrollmentId={headerCase.enrollment_id ?? undefined}
          episodeId={headerCase.id}
          onNavigate={onNavigate}
          onEpisodeChange={handleEpisodeChange}
        />
      )}

      {panel ? (
        renderPanel(panel)
      ) : (
        <>
          {/* One toolbar: Filter · Case · Add */}
          <div className="flex flex-wrap items-center gap-2">
            <Popover open={filterOpen} onOpenChange={setFilterOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="!border !border-v2border !text-ink hover:!bg-paper-2">
                  <Filter className="w-4 h-4 mr-1.5 text-ink-500" />
                  {t('memberWork.filter.button', 'Filter')}
                  {activeFilterCount > 0 && (
                    <span className="ml-1 rounded-full bg-v2accent px-1.5 text-[10px] font-semibold text-white">
                      {activeFilterCount}
                    </span>
                  )}
                  <ChevronDown className="w-3.5 h-3.5 ml-1 text-ink-500" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-80 p-0">
                <div className="max-h-[70vh] overflow-y-auto p-3 space-y-4">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-500 mb-1.5">
                      {t('memberWork.filter.types', 'Record types')}
                    </div>
                    <ul className="space-y-1">
                      {shownTypes.map((c) => (
                        <li key={c.kind}>
                          <label className="flex items-center gap-2 rounded px-1 py-1 text-sm text-ink hover:bg-paper-2 cursor-pointer">
                            <Checkbox
                              checked={selectedTypes.includes(c.kind)}
                              onCheckedChange={() => toggleType(c.kind)}
                            />
                            <span className="flex-1">{c.label}</span>
                            {counts && <span className="text-xs tabular-nums text-ink-400">{counts[c.kind] ?? 0}</span>}
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {flags.activities && (activeCategories.length > 0 || canManageCategories) && (
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-500 mb-1.5">
                        {t('memberWork.filter.noteCategories', 'Note categories')}
                      </div>
                      <ul className="space-y-1">
                        {activeCategories.map((c) => (
                          <li key={c.id}>
                            <label className="flex items-center gap-2 rounded px-1 py-1 text-sm text-ink hover:bg-paper-2 cursor-pointer">
                              <Checkbox
                                checked={filters.categoryId === c.id}
                                onCheckedChange={() => toggleCategory(c.id)}
                              />
                              <Tag className="w-3.5 h-3.5 text-ink-400" />
                              <span className="flex-1">{c.name}</span>
                            </label>
                          </li>
                        ))}
                      </ul>
                      {activeCategories.length > 0 && (
                        <p className="mt-1 text-[11px] text-ink-400">
                          {t('memberWork.filter.categoryHint', 'A category shows notes only.')}
                        </p>
                      )}
                      {canManageCategories && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className={`${INK_GHOST_BUTTON} mt-1 !px-1`}
                          onClick={() => {
                            setFilterOpen(false);
                            setManageCategoriesRequest(Date.now());
                          }}
                        >
                          <Settings2 className="w-3.5 h-3.5 mr-1" />
                          {t('memberWork.filter.manageCategories', 'Manage categories')}
                        </Button>
                      )}
                    </div>
                  )}

                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-500 mb-1.5">
                      {t('memberWork.filter.dates', 'Date range')}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="text-xs text-ink-500 space-y-1">
                        <span>{t('memberWork.filter.from', 'From')}</span>
                        <Input
                          type="date"
                          value={filters.dateFrom}
                          max={filters.dateTo || undefined}
                          onChange={(e) => updateFilters({ ...filters, dateFrom: e.target.value })}
                        />
                      </label>
                      <label className="text-xs text-ink-500 space-y-1">
                        <span>{t('memberWork.filter.to', 'To')}</span>
                        <Input
                          type="date"
                          value={filters.dateTo}
                          min={filters.dateFrom || undefined}
                          onChange={(e) => updateFilters({ ...filters, dateTo: e.target.value })}
                        />
                      </label>
                    </div>
                  </div>
                </div>
                <div className="border-t border-v2border px-3 py-2 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className={INK_GHOST_BUTTON}
                      onClick={() => void saveFiltersAsDefault()}
                      disabled={defaultStatus === 'saving'}
                      data-testid="activity-filter-save-default"
                    >
                      <Bookmark className="w-3.5 h-3.5 mr-1" />
                      {t('memberWork.filter.saveDefault', 'Save as my default')}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className={INK_GHOST_BUTTON}
                      onClick={() => void resetFiltersDefault()}
                      disabled={defaultStatus === 'saving' || (!hasSavedDefault && activeFilterCount === 0)}
                      data-testid="activity-filter-reset-default"
                    >
                      <RotateCcw className="w-3.5 h-3.5 mr-1" />
                      {t('memberWork.filter.resetDefault', 'Reset')}
                    </Button>
                  </div>
                  {defaultStatus !== 'idle' && defaultStatus !== 'saving' && (
                    <p
                      className={`text-xs ${defaultStatus === 'error' ? 'text-red-600' : 'text-ink-500'}`}
                      role="status"
                    >
                      {defaultStatus === 'saved'
                        ? t('memberWork.filter.defaultSaved', 'Saved. This filter opens by default on every device.')
                        : defaultStatus === 'reset'
                          ? t('memberWork.filter.defaultReset', 'Your saved default was removed.')
                          : t('memberWork.filter.defaultError', 'Could not save your default. Your filter still applies here.')}
                    </p>
                  )}
                  <div className="flex items-center justify-between">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className={INK_GHOST_BUTTON}
                      onClick={clearFilters}
                      disabled={activeFilterCount === 0}
                    >
                      {t('memberWork.filter.clear', 'Clear')}
                    </Button>
                    <Button type="button" size="sm" className={ACCENT_BUTTON} onClick={() => setFilterOpen(false)}>
                      {t('memberWork.filter.done', 'Done')}
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {caseUi && visibleCases.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="!border !border-v2border !text-ink hover:!bg-paper-2">
                    <Briefcase className="w-4 h-4 mr-1.5 text-ink-500" />
                    {t('memberWork.caseFilter', 'Case: {{name}}', { name: caseFilterLabel })}
                    <ChevronDown className="w-3.5 h-3.5 ml-1 text-ink-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuLabel>{t('memberWork.caseFilterLabel', 'Show records from')}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => chooseCase('all')}>{t('memberWork.allCases', 'All')}</DropdownMenuItem>
                  {visibleCases.map((c) => (
                    <DropdownMenuItem key={c.id} onSelect={() => chooseCase(c.id)}>
                      {caseName(c)}
                      <span className="ml-2 text-xs text-ink-400">
                        {String(c.status).toLowerCase()}
                        {multiProgram && c.program_name ? ` · ${c.program_name}` : ''}
                      </span>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuItem onSelect={() => chooseCase('none')}>{t('memberWork.noCase', 'No case')}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {(visibleAdd.length > 0 || visibleComms.length > 0 || canAddCase) && (
              <div className="ml-auto">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" className={ACCENT_BUTTON}>
                      <Plus className="w-4 h-4 mr-1.5" />
                      {t('memberWork.add.button', 'Add')}
                      <ChevronDown className="w-3.5 h-3.5 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {visibleAdd.map(renderAddItem)}
                    {visibleComms.length > 0 && (
                      <>
                        {visibleAdd.length > 0 && <DropdownMenuSeparator />}
                        {visibleComms.map(renderAddItem)}
                      </>
                    )}
                    {canAddCase && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={() => setPanel({ type: 'new-case' })}>
                          <FilePlus className="w-4 h-4 mr-2 text-ink-500" />
                          {t('memberWork.add.case', 'New case')}
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>

          {/* Active filters — only when something is selected. */}
          {activeFilterCount > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              {!activeCategory && selectedTypes.map((k) => chip(`type-${k}`, typeLabel(k), () => toggleType(k)))}
              {activeCategory &&
                chip(
                  'category',
                  <>
                    <Tag className="w-3 h-3 text-ink-400" />
                    {t('memberWork.filter.notesIn', 'Notes: {{name}}', { name: activeCategory.name })}
                  </>,
                  () => updateFilters({ ...filters, categoryId: null, types: [] }),
                )}
              {(filters.dateFrom || filters.dateTo) &&
                chip(
                  'dates',
                  <>
                    <CalendarRange className="w-3 h-3 text-ink-400" />
                    {dateChipLabel}
                  </>,
                  () => updateFilters({ ...filters, dateFrom: '', dateTo: '' }),
                )}
              <Button type="button" variant="ghost" size="sm" className={`${INK_GHOST_BUTTON} !px-1.5 !py-0.5`} onClick={clearFilters}>
                {t('memberWork.filter.clearAll', 'Clear all')}
              </Button>
            </div>
          )}

          {caseForNew && (
            <p className="text-xs text-ink-500">
              {t('memberWork.addsToCase', 'New records are filed under Case #{{number}}.', { number: caseForNew.case_number })}
            </p>
          )}
          {caseUi && !casesFromFeed && caseFilter !== 'all' && (
            <p className="text-xs text-ink-500">
              {t('memberWork.caseFilterUnavailable', 'This list cannot be filtered by case yet, so it shows everything.')}
            </p>
          )}
          {openError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/30 dark:border-red-900 dark:text-red-300">
              {openError}
            </div>
          )}

          <MemberActivityFeed
            memberId={memberId}
            enrollmentId={caseForNew ? addEnrollment?.id : undefined}
            scope={{
              caseFilter: caseUi ? caseFilter : 'all',
              types: requestTypes,
              cases: visibleCases,
              canMove: caseUi && canMove,
              showCases: caseUi,
              caseEpisodeId: caseIdForNew,
              categoryId: activeCategory?.id ?? null,
              dateFrom: filters.dateFrom || null,
              dateTo: filters.dateTo || null,
              manageCategoriesRequest,
              onCategories: handleCategories,
              refreshKey,
              onResponse: handleFeedResponse,
              onOpenItem: (item) => void openItem(item),
              canOpen,
            }}
          />
        </>
      )}

    </div>
  );
}
